import React, { useState, useEffect, useMemo } from 'react';
import { View, LogBox, ScrollView } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import CustomDT from '../common/controls/customDataTable';
import PieChartComp from '../common/charts/piChart-builder';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import { Loader } from '../../Common/Loader';
import { colorType5, logStyles } from './LogStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import LogKpiRow from './LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import { useNav } from '../../../providers/NavProvider';

interface Props {
  siteId: string;
  logTitle: string;
}

export default function HazardousMaterialStorage(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);

  const [LogData, setLogData] = useState<null | any>(null);
  const [LogColumns, setLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  const { sitesResp } = useNav();

  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const logTitle = props.logTitle;
          const accessToken = await getAccessToken();
          const json = await getAllItems(logTitle, accessToken, clientAPIURL, props.siteId, undefined, packageSite.id);
          setLogData(json.items);
          setLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [sitesResp]);

  return LogData && LogColumns
    ? <CreateLogCharts LogData={LogData} LogColumns={LogColumns} siteId={props.siteId} logTitle={props.logTitle} clientAPIURL={clientAPIURL} />
    : <Loader />;
}

interface CreateLogChartProps {
  LogData: any;
  LogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
}

const CreateLogCharts: React.FC<CreateLogChartProps> = ({ LogData, LogColumns, siteId, logTitle, clientAPIURL }) => {

  const { getAccessToken } = useAuth();
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';

  const ColumnsWithIDColumn = LogColumns && [
    ...LogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const [filteredData, setFilteredData] = useState<any[]>(LogData ?? []);

  useEffect(() => {
    setFilteredData(LogData ?? []);
  }, [LogData]);

  function returnDate(value: Date) {
    if (value) {
      const date = new Date(value);
      const timezoneOffset = 4 * 60;
      const adjustedDate = new Date(date.getTime() + timezoneOffset * 60000);
      const day = adjustedDate.getDate();
      const month = adjustedDate.getMonth() + 1;
      const year = adjustedDate.getFullYear();
      return `${day < 10 ? '0' + day : day}/${month < 10 ? '0' + month : month}/${year}`;
    } else {
      return value;
    }
  }

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        const updatedDate = result.items[0] && returnDate(result.items[0].Date);
        const newItem = { ...result.items[0], Date: updatedDate };
        setFilteredData(prev => {
          const without = prev.filter((oItem: any) => oItem.ID !== item.ID);
          return [...without, newItem];
        });
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  const chartData = useMemo(() => {
    const colorScale1 = scaleOrdinal(schemeCategory10);

    // --- Pie: Material Qty (Liters)/Type ---
    const pieAccum: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      if (item.MaterialType == null) return;
      const key = String(item.MaterialType);
      pieAccum[key] = (pieAccum[key] ?? 0) + (Number(item.StoredQuantityLiters) || 0);
    });
    // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
    const byMaterialTypePieData = Object.entries(pieAccum).map(([name, value], index) => ({
      name,
      value: Math.round(value),
      color: colorType5(index.toString()),
    }));

    // --- Line: Material Qty (Liters)/Type by Period ---
    const uniquePeriod = [...new Set(filteredData.map((item: any) => item.Period))].filter(Boolean) as string[];
    const uniqueTypes = [...new Set(filteredData.map((item: any) => item.MaterialType))].filter(Boolean) as string[];

    const periodTypeSums: Record<string, Record<string, number>> = {};
    filteredData.forEach((item: any) => {
      if (!item.Period || item.MaterialType == null) return;
      const p = String(item.Period);
      const t = String(item.MaterialType);
      if (!periodTypeSums[p]) periodTypeSums[p] = {};
      periodTypeSums[p][t] = (periodTypeSums[p][t] ?? 0) + (Number(item.StoredQuantityLiters) || 0);
    });

    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineDatasets = uniqueTypes.map((type, index) => ({
      data: uniquePeriod.map(period => Math.round(periodTypeSums[period]?.[type] ?? 0)),
      color: (_opacity = 1) => colorScale1(String(index)),
      strokeWidth: 2,
    }));
    const lineData = {
      labels: uniquePeriod,
      datasets: lineDatasets,
      legend: uniqueTypes.map(type => ({ name: type })),
    };

    // --- Summary table: period × materialType ---
    const materialTypeLabels = LogColumns?.find((col: any) => col.columnName === 'MaterialType')?.choiceChoices;
    const materialTypesTable: string[] = materialTypeLabels
      ? materialTypeLabels.split('#;#').map((v: string) => v.split('#;#')[0])
      : uniqueTypes;

    const groupedTable: Record<string, any> = {};
    filteredData.forEach((item: any) => {
      const { Period, MaterialType, StoredQuantityLiters } = item;
      if (!Period) return;
      if (!groupedTable[Period]) {
        groupedTable[Period] = { Period };
        materialTypesTable.forEach((t: string) => { groupedTable[Period][t] = 0; });
      }
      if (MaterialType != null) {
        const t = String(MaterialType);
        groupedTable[Period][t] = (groupedTable[Period][t] ?? 0) + Math.round(Number(StoredQuantityLiters) || 0);
      }
    });

    const mergedData = Object.values(groupedTable).map((row: any) => {
      const total = materialTypesTable.reduce((s: number, t: string) => s + (row[t] ?? 0), 0);
      return { ...row, 'Total (Material Type)': total };
    });

    const columnKeys = mergedData.length > 0
      ? Object.keys(mergedData[0]).map((key, index) => ({ ColumnName: key, Order: index }))
      : [];

    // --- KPI ---
    const totalStored = filteredData.reduce((s: number, item: any) => s + (Number(item.StoredQuantityLiters) || 0), 0);

    return { byMaterialTypePieData, lineData, uniquePeriod, mergedData, columnKeys, totalStored };
  }, [filteredData, LogColumns]);

  const kpiItems = [
    { label: 'Total Stored (L)', value: Math.round(chartData.totalStored), color: '#00695C' },
    { label: 'Entries', value: filteredData.length, color: '#4527A0' },
  ];

  const kpiRow = (
    <View style={{ zIndex: 200 }}>
      <LogKpiRow items={kpiItems}>
        <LogDateRangeFilter data={LogData} onFilterChange={setFilteredData} />
      </LogKpiRow>
    </View>
  );

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab && defaultTab === 'list' ? 2 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              {kpiRow}
              <View>
                <View style={[logStyles.container]}>
                  <View key={1} style={[logStyles.card, { backgroundColor: '' }]}>
                    {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                    <PieChartComp data={chartData.byMaterialTypePieData} colorPallate={interpolateBlues} subTitle={'(Cumulative)'} ChartTitle={"Material Qty (Liters)/Type"} />
                  </View>
                  <View key={2} style={[logStyles.card, { backgroundColor: '' }]}>
                    {/* LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string */}
                    <LineChartScreen legend={chartData.lineData.legend} data={chartData.lineData} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"Material Qty (Liters)/Type"} />
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Tables" icon="table">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {chartData.mergedData && chartData.columnKeys && (
                      <CustomDT
                        data={chartData.mergedData}
                        columns={chartData.columnKeys}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId={'StoredQuantities'}
                        tableTitle={"Hazardous Materials Stored Quantities (Liters)"}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {filteredData && ColumnsWithIDColumn && (
                      <PaperTableDynamic
                        data={filteredData}
                        columns={ColumnsWithIDColumn}
                        siteId={siteId}
                        logTitle={logTitle}
                        updateData={handleDataUpdate}
                        isConsolidatedTable={false}
                        funcSetShowFilter={() => {}}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
        </Tabs>
      </TabsProvider>
    </View>
  );
};
